import { useState, useEffect } from "react";
import { AlertTriangle, ArrowRight } from "lucide-react";

import { LeadDrilldownModal } from "@/components/LeadDrilldownModal";

import { EmptyState } from "@/components/EmptyState";
import { KpiCard } from "@/components/KpiCard";
import { Panel } from "@/components/Panel";
import { StatusPill } from "@/components/StatusPill";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  safeRatio,
  statusTone,
} from "@/lib/format";
import { cn } from "@/lib/cn";
import { usePayload } from "@/lib/usePayload";
import { getTotals, getPaidSocialEfficiency, getPaidSearchEfficiency, getFilteredBreakdown } from "@/lib/viewModeSelectors";

export function Overview() {
  const { payload, isLoading, viewMode, monthLabel, monthKey } = usePayload();
  if (!payload) return <EmptyState title="No data yet for this period" loading={isLoading} />;

  const t = getTotals(payload, viewMode);
  const ai = payload.ai_report;
  const overallTone = statusTone(ai?.summary_cards?.overall_health || "");
  const findings = (ai?.key_findings || []).slice(0, 3);

  const [modalStage, setModalStage] = useState<string | null>(null);
  
  const breakdown = getFilteredBreakdown(payload, viewMode);
  const allLeads = Object.entries(breakdown).flatMap(([source, rows]) =>
    ((rows as any) || []).map((row: any) => ({ ...row, source }))
  );

  const socialEfficiency = getPaidSocialEfficiency(payload, viewMode);
  const searchEfficiency = getPaidSearchEfficiency(payload, viewMode);
  const displayLabel = viewMode === "current_week" ? (payload.weekly_reporting?.week_label || "Current Week") : monthLabel;

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="font-display text-xl sm:text-2xl font-semibold text-ink-900">Overview</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Executive summary for {displayLabel}</p>
      </div>

      {ai?.summary_cards && (
        <AlertBanner
          status={ai.summary_cards.overall_health}
          message={ai.summary_cards.biggest_risk}
          tone={overallTone}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <KpiCard
          accent="blue"
          label="Total MQL"
          value={formatNumber(t.mql)}
          sublabel="Marketing Qualified Leads"
          tooltip="Marketing Qualified Lead"
          onClick={() => setModalStage("MQL")}
        />
        <KpiCard
          accent="cyan"
          label="Total SQL"
          value={formatNumber(t.sql)}
          sublabel={t.mql ? `${formatPercent(safeRatio(t.sql, t.mql))} of MQL` : undefined}
          tooltip="Sales Qualified Lead - a lead for whom a product demo has been completed."
          onClick={() => setModalStage("SQL")}
        />
        <KpiCard
          accent="indigo"
          label="Total Demo Done"
          value={formatNumber(t.demo_dones || 0)}
          sublabel={t.sql ? `${formatPercent(safeRatio(t.demo_dones, t.sql))} of SQL` : undefined}
          tooltip="Includes leads currently in 'Demo Done' and those that progressed to SQO/Win/Lost."
          onClick={() => setModalStage("Demo Done")}
        />
        <KpiCard
          accent="teal"
          label="Total SQO"
          value={formatNumber(t.sqo)}
          sublabel={t.sql ? `${formatPercent(safeRatio(t.sqo, t.sql))} of SQL` : undefined}
          tooltip="Sales Qualified Opportunity - demo completed and payment has been scheduled."
          onClick={() => setModalStage("SQO")}
        />
        <KpiCard
          accent="green"
          label="Total Wins"
          value={formatNumber(t.wins)}
          sublabel={t.sqo ? `${formatPercent(safeRatio(t.wins, t.sqo))} close rate` : undefined}
          onClick={() => setModalStage("Win")}
        />
        <KpiCard
          accent="green"
          label="Total Revenue"
          value={formatCurrency(t.revenue, { compact: true })}
          sublabel={viewMode === "current_week" ? "This week" : "This month"}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ConversionFunnel totals={t} monthStr={monthKey} />
        <AIAnalysisCard
          summary={ai?.summary_cards}
          findings={findings}
          overallTone={overallTone}
        />
      </div>

      <ChannelEfficiencySnapshot
        social={socialEfficiency}
        search={searchEfficiency}
      />

      {modalStage && (
        <LeadDrilldownModal
          stage={modalStage}
          leads={allLeads}
          monthStr={monthKey}
          onClose={() => setModalStage(null)}
        />
      )}
    </div>
  );
}

function AlertBanner({
  status,
  message,
  tone,
}: {
  status: string;
  message: string;
  tone: "good" | "warn" | "bad" | "muted";
}) {
  const wrap =
    tone === "good"
      ? "border-green-200 bg-green-50/70"
      : tone === "warn"
        ? "border-amber-200 bg-amber-50/70"
        : tone === "bad"
          ? "border-red-200 bg-red-50/70"
          : "border-slate-200 bg-slate-50";
  const accent =
    tone === "good" ? "text-green-700" : tone === "warn" ? "text-amber-700" : tone === "bad" ? "text-red-700" : "text-slate-600";
  return (
    <div className={cn("rounded-card border px-4 py-3 flex gap-3 items-start", wrap)}>
      <AlertTriangle className={cn("h-4 w-4 mt-0.5 shrink-0", accent)} />
      <div className="text-sm text-ink-900">
        <span className={cn("font-semibold", accent)}>{status}: </span>
        <span className="text-slate-700">{message}</span>
      </div>
    </div>
  );
}

function ConversionFunnel({ totals, monthStr }: { totals: { mql: number; sql: number; sqo: number; wins: number }; monthStr: string }) {
  const max = Math.max(totals.mql, totals.sql, totals.sqo, totals.wins, 1);
  const rows = [
    { label: "MQL", value: totals.mql, color: "bg-accent-blue", text: "text-blue-50" },
    { label: "SQL", value: totals.sql, color: "bg-accent-cyan", text: "text-cyan-50" },
    { label: "SQO", value: totals.sqo, color: "bg-accent-teal", text: "text-teal-50" },
    { label: "Wins", value: totals.wins, color: "bg-accent-green", text: "text-green-50" },
  ];

  const winRate = safeRatio(totals.wins, totals.mql);
  const mqlToSql = safeRatio(totals.sql, totals.mql);

  // Fetch SQL Quality Summary data
  const normalizedMonth = monthStr.replace('/', '-');
  const [qcData, setQcData] = useState<{ totalSqls: number; callsFound: number; highQuality: number; avgFoundScore: number } | null>(null);

  useEffect(() => {
    fetch(`/api/fathom/quality-check?month=${normalizedMonth}`)
      .then((res) => res.json())
      .then((data) => {
        const results = data.results || [];
        const totalSqls = totals.sql || results.length;
        // Calls found in Fathom (having a fathom meeting ID or recording URL)
        const found = results.filter((r: any) => r.fathom_meeting_id || r.fathom_recording_url || r.quality_band === "High");
        const callsFound = found.length;
        const highQuality = found.filter((r: any) => r.quality_band === "High" || (r.overall_quality_score || 0) >= 8).length;
        
        // Quality score of found calls only
        const foundScores = found.map((r: any) => r.overall_quality_score).filter((s: any) => typeof s === "number");
        const avgFoundScore = foundScores.length > 0 ? (foundScores.reduce((a: number, b: number) => a + b, 0) / foundScores.length) : 0;
        
        setQcData({
          totalSqls,
          callsFound,
          highQuality,
          avgFoundScore: Math.round(avgFoundScore * 10) / 10
        });
      })
      .catch(() => {});
  }, [normalizedMonth, totals.sql]);

  return (
    <Panel title="Conversion Funnel" subtitle="MQL → SQL → SQO → Win">
      <div className="space-y-3">
        {rows.map((r, i) => {
          const widthPct = (r.value / max) * 100;
          const conv =
            i === 0
              ? null
              : safeRatio(r.value, totals.mql);
          return (
            <div key={r.label} className="grid grid-cols-[60px_1fr_60px] items-center gap-3">
              <div className="text-xs font-medium text-slate-600">{r.label}</div>
              <div className="h-7 bg-slate-100 rounded-md overflow-hidden relative">
                <div
                  className={cn(r.color, "h-full flex items-center px-2 transition-all")}
                  style={{ width: `${Math.max(widthPct, r.value > 0 ? 4 : 0)}%` }}
                >
                  {r.value > 0 && (
                    <span className={cn("text-xs font-semibold numeric-value", r.text)}>
                      {formatNumber(r.value)}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-xs text-slate-500 numeric-value text-right">
                {conv != null ? formatPercent(conv) : ""}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 flex-wrap mt-4 pt-4 border-t border-border">
        <Legend label={`Overall Win Rate: ${winRate != null ? formatPercent(winRate) : "—"}`} dotClass="bg-slate-700" />
        <Legend label={`MQL→SQL: ${mqlToSql != null ? formatPercent(mqlToSql) : "—"}`} dotClass="bg-amber-500" />
      </div>

      {/* SQL Quality Summary Card */}
      <div className="mt-4 pt-3 border-t border-slate-100 bg-slate-50/70 -mx-4 -mb-4 p-4 rounded-b-card">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-cyan-500"></span>
            SQL Quality Summary (Fathom AI)
          </span>
          {qcData && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
              Avg Call Quality: {qcData.avgFoundScore}/10
            </span>
          )}
        </div>
        {qcData ? (
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="bg-white p-2 rounded border border-slate-200">
              <div className="text-slate-500 text-[10px] uppercase tracking-wide">Total SQLs</div>
              <div className="font-bold text-slate-900 text-sm mt-0.5">{qcData.totalSqls}</div>
            </div>
            <div className="bg-white p-2 rounded border border-slate-200">
              <div className="text-blue-600 text-[10px] uppercase tracking-wide font-semibold">Calls Matched</div>
              <div className="font-bold text-blue-700 text-sm mt-0.5">{qcData.callsFound} / {qcData.totalSqls}</div>
            </div>
            <div className="bg-white p-2 rounded border border-slate-200">
              <div className="text-emerald-600 text-[10px] uppercase tracking-wide font-semibold">High Quality Demos</div>
              <div className="font-bold text-emerald-700 text-sm mt-0.5">{qcData.highQuality}</div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-slate-500 italic">Audit data loading...</div>
        )}
      </div>
    </Panel>
  );
}

function Legend({ label, dotClass }: { label: string; dotClass: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className={cn("h-1.5 w-1.5 rounded-full", dotClass)} />
      <span className="text-slate-600">{label}</span>
    </div>
  );
}

function AIAnalysisCard({
  summary,
  findings,
  overallTone,
}: {
  summary?: { best_paid_channel: string; biggest_risk: string; overall_health: string };
  findings: { area: string; status: string; insight: string; action: string }[];
  overallTone: "good" | "warn" | "bad" | "muted";
}) {
  if (!summary && findings.length === 0) {
    return (
      <Panel title="AI Analysis" subtitle="Monthly intelligence summary">
        <div className="text-sm text-slate-500 py-4">No AI commentary available for this run.</div>
      </Panel>
    );
  }
  return (
    <Panel
      title="AI Analysis"
      subtitle="Monthly intelligence summary"
      right={summary?.overall_health ? <StatusPill label={summary.overall_health} tone={overallTone} /> : null}
    >
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <HighlightCard tone="good" label="Best Channel" value={summary.best_paid_channel} />
          <HighlightCard tone="bad" label="Biggest Risk" value={summary.biggest_risk} />
        </div>
      )}
      <div className="space-y-4">
        {findings.map((f, idx) => {
          const tone = statusTone(f.status);
          return (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-[160px_1fr_220px] gap-4 items-start">
              <div>
                <div className="text-sm font-semibold text-ink-900">{f.area}</div>
                <StatusPill label={f.status} tone={tone} className="mt-1.5" />
              </div>
              <div className="text-sm text-slate-700 leading-relaxed">{f.insight}</div>
              <a className="text-sm text-accent-blue hover:underline flex items-start gap-1 leading-snug" href="#">
                <ArrowRight className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{f.action}</span>
              </a>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

function HighlightCard({ tone, label, value }: { tone: "good" | "bad"; label: string; value: string }) {
  const wrap = tone === "good" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200";
  const accent = tone === "good" ? "text-green-700" : "text-red-700";
  return (
    <div className={cn("rounded-md border px-3.5 py-2.5", wrap)}>
      <div className={cn("label-xs", accent)}>{label}</div>
      <div className="text-sm font-semibold text-ink-900 mt-1">{value}</div>
    </div>
  );
}

function ChannelEfficiencySnapshot({
  social,
  search,
}: {
  social: import("@/lib/types").PaidSocialEfficiency;
  search: import("@/lib/types").PaidSearchEfficiency;
}) {
  const rows = [
    {
      label: "Paid Social",
      dotClass: "bg-violet-500",
      spend: social.spend ?? social.meta_spend,
      mql: social.paid_social_mql,
      revenue: social.paid_social_revenue,
      cac: social.cac,
      roas: social.roas,
    },
    {
      label: "Paid Search",
      dotClass: "bg-blue-500",
      spend: search.google_spend,
      mql: search.paid_search_mql,
      revenue: search.paid_search_revenue,
      cac: search.cac,
      roas: search.roas,
    },
  ];
  return (
    <Panel title="Channel Efficiency Snapshot" subtitle="Paid Social vs. Paid Search KPIs">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {rows.map((r) => (
          <div key={r.label} className="rounded-md border border-border p-4">
            <div className="flex items-center gap-2">
              <span className={cn("h-2 w-2 rounded-full", r.dotClass)} />
              <span className="text-sm font-semibold">{r.label}</span>
              <span className="ml-auto text-xs text-slate-500 numeric-value">
                {r.roas != null ? `${r.roas.toFixed(2)}× ROAS` : "—"}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <Stat label="Spend" value={formatCurrency(r.spend, { compact: true })} />
              <Stat label="MQLs" value={formatNumber(r.mql)} />
              <Stat label="Revenue" value={formatCurrency(r.revenue, { compact: true })} />
            </div>
            <div className="mt-3 text-xs text-slate-500">
              CAC: <span className="numeric-value text-ink-900">{formatCurrency(r.cac, { compact: true })}</span>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="label-xs">{label}</div>
      <div className="num-display text-xl mt-1 leading-none numeric-value">{value}</div>
    </div>
  );
}
